/**
 * <aramgah-spatial-landscape>
 *
 * Lunar sleep landscape: 90-day chronotype regression as a starfield
 * (each dot = one night, color = sleep-stage mix), lunar phase dial as
 * a slowly-orbiting moon, brahma-muhurta amber light cone at horizon.
 * Audio: binaural delta (8 Hz simulated via stereo beat) for ambient.
 *
 * Inline 2D fallback when WebXR is missing.
 */

const THREE_CDN = "https://esm.sh/three@0.170.0";

interface ThreeModule {
  Scene: new () => { add(o: unknown): void; background: unknown };
  PerspectiveCamera: new (fov: number, aspect: number, near: number, far: number) => {
    position: { set(x: number, y: number, z: number): void };
    aspect: number;
    updateProjectionMatrix(): void;
    lookAt(x: number, y: number, z: number): void;
  };
  WebGLRenderer: new (params?: Record<string, unknown>) => {
    setSize(w: number, h: number): void;
    setPixelRatio(r: number): void;
    setAnimationLoop(cb: ((t: number) => void) | null): void;
    render(scene: unknown, camera: unknown): void;
    domElement: HTMLCanvasElement;
    xr: { enabled: boolean; setSession(s: XRSession): Promise<void> };
    dispose(): void;
  };
  Mesh: new (geometry: unknown, material: unknown) => {
    position: { set(x: number, y: number, z: number): void };
    rotation: { y: number; x: number; z: number };
  };
  Points: new (geometry: unknown, material: unknown) => {
    position: { set(x: number, y: number, z: number): void };
    rotation: { y: number; x: number };
  };
  BufferGeometry: new () => { setAttribute(name: string, attr: unknown): void };
  BufferAttribute: new (array: Float32Array, itemSize: number) => unknown;
  PointsMaterial: new (params?: Record<string, unknown>) => unknown;
  SphereGeometry: new (r: number, ws: number, hs: number) => unknown;
  ConeGeometry: new (r: number, h: number, rs: number, hs?: number, open?: boolean, thetaStart?: number, thetaLength?: number) => unknown;
  PlaneGeometry: new (w: number, h: number) => unknown;
  MeshStandardMaterial: new (params?: Record<string, unknown>) => unknown;
  MeshBasicMaterial: new (params?: Record<string, unknown>) => unknown;
  PointLight: new (color: number, intensity?: number, distance?: number) => { position: { set(x: number, y: number, z: number): void } };
  AmbientLight: new (color: number, intensity?: number) => unknown;
  DirectionalLight: new (color: number, intensity?: number) => { position: { set(x: number, y: number, z: number): void } };
  Group: new () => { add(child: unknown): void; rotation: { y: number; x: number }; position: { set(x: number, y: number, z: number): void } };
  Color: new (hex: string | number) => unknown;
}

let threeCache: ThreeModule | null = null;
async function loadThree(): Promise<ThreeModule> {
  if (threeCache) return threeCache;
  threeCache = (await import(/* @vite-ignore */ THREE_CDN)) as unknown as ThreeModule;
  return threeCache;
}

interface StarfieldPoints {
  rotation: { y: number; x: number };
  position: { set(x: number, y: number, z: number): void };
}

function generateStarfield(THREE: ThreeModule, nights: number = 90): { points: StarfieldPoints } {
  const positions = new Float32Array(nights * 3);
  const colors = new Float32Array(nights * 3);
  for (let i = 0; i < nights; i++) {
    // Spiral spread in the night sky dome
    const theta = (i / nights) * Math.PI * 6;
    const phi = Math.acos(1 - 2 * (i / nights));
    const r = 6 + (i % 7) * 0.1;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi) * 0.5 + 2.5;
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);

    // Color by simulated sleep-stage mix:
    //  deep -> indigo, light -> pearl, rem -> amber
    const deepRatio = 0.4 + 0.3 * Math.sin(i * 0.21);
    const remRatio = 0.2 + 0.2 * Math.cos(i * 0.13);
    colors[i * 3] = 0.4 + 0.5 * remRatio;
    colors[i * 3 + 1] = 0.35 + 0.4 * (1 - deepRatio);
    colors[i * 3 + 2] = 0.55 + 0.3 * deepRatio;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geom.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.PointsMaterial({ size: 0.08, vertexColors: true, transparent: true, opacity: 0.85, sizeAttenuation: true });
  return { points: new THREE.Points(geom, mat) as unknown as StarfieldPoints };
}

async function buildLunarScene(THREE: ThreeModule): Promise<{ scene: unknown; camera: ReturnType<ThreeModule["PerspectiveCamera"]["prototype"]["constructor"]> extends infer C ? C : never; tick: (t: number) => void }> {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#080a18");

  const camera = new THREE.PerspectiveCamera(62, 1, 0.05, 50);
  camera.position.set(0, 1.6, 4.5);
  camera.lookAt(0, 2.5, 0);

  scene.add(new THREE.AmbientLight(0x556080, 0.4));
  const moonLight = new THREE.PointLight(0xe6ecf6, 1.3, 18);
  moonLight.position.set(2.5, 4.5, -2);
  scene.add(moonLight);

  const starfield = generateStarfield(THREE);
  scene.add(starfield.points);

  // Moon
  const moonGroup = new THREE.Group();
  const moon = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 32, 24),
    new THREE.MeshStandardMaterial({
      color: new THREE.Color("#e9eaf0"),
      emissive: new THREE.Color("#33365a"),
      emissiveIntensity: 0.3,
      roughness: 0.85,
    }),
  );
  moon.position.set(2.5, 4.5, -2);
  moonGroup.add(moon);
  scene.add(moonGroup);

  // Brahma-muhurta amber light cone at horizon (east)
  const coneMat = new THREE.MeshBasicMaterial({ color: new THREE.Color("#f0a64a"), transparent: true });
  const cone = new THREE.Mesh(new THREE.ConeGeometry(1.4, 3.2, 24, 1, true), coneMat);
  cone.position.set(-3.5, 1.6, 0);
  cone.rotation.z = Math.PI / 2;
  scene.add(cone);

  // Horizon plane
  const horizonMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color("#1a1a2a"),
    roughness: 0.95,
  });
  const horizon = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), horizonMat);
  horizon.rotation.x = -Math.PI / 2;
  scene.add(horizon);

  let lastT = 0;
  const tick = (t: number): void => {
    const dt = (t - lastT) / 1000;
    lastT = t;
    if (Number.isFinite(dt)) {
      starfield.points.rotation.y += dt * 0.008;
      moonGroup.rotation.y += dt * 0.012;
    }
  };
  return { scene, camera, tick };
}

async function playBinauralDelta(): Promise<void> {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    // Stereo beat: left 192 Hz, right 200 Hz -> 8 Hz delta beat
    const oscL = ctx.createOscillator(); oscL.type = "sine"; oscL.frequency.value = 192;
    const oscR = ctx.createOscillator(); oscR.type = "sine"; oscR.frequency.value = 200;
    const merger = ctx.createChannelMerger(2);
    const gainL = ctx.createGain(); gainL.gain.value = 0.04;
    const gainR = ctx.createGain(); gainR.gain.value = 0.04;
    oscL.connect(gainL).connect(merger, 0, 0);
    oscR.connect(gainR).connect(merger, 0, 1);
    merger.connect(ctx.destination);
    oscL.start(); oscR.start();
    setTimeout(() => { oscL.stop(); oscR.stop(); }, 600_000);
  } catch {
    // silent
  }
}

class AramgahSpatialLandscape extends HTMLElement {
  private mounted = false;
  private currentSession: XRSession | null = null;
  connectedCallback(): void { if (!this.mounted) { this.mounted = true; void this.render(); } }
  disconnectedCallback(): void { void this.currentSession?.end(); }

  private async render(): Promise<void> {
    const xr = navigator.xr;
    const vrSupported = xr ? await xr.isSessionSupported("immersive-vr").catch(() => false) : false;
    const arSupported = xr ? await xr.isSessionSupported("immersive-ar").catch(() => false) : false;
    const supported = vrSupported || arSupported;

    this.innerHTML = `
      <style>
        aramgah-spatial-landscape {
          display: block; margin: 1.5rem 0; padding: 1.4rem 1.5rem;
          background: linear-gradient(180deg, #161823 0%, #0d0e18 100%);
          color: #e3e6f0; border-radius: 14px;
          font-family: 'EB Garamond', 'Tiro Devanagari Hindi', serif;
          border: 1px solid #2a2e44;
        }
        aramgah-spatial-landscape header { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; }
        aramgah-spatial-landscape h3 { margin: 0; font-size: 1.1rem; font-weight: 600; }
        aramgah-spatial-landscape .badge { font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.6; color: #b2bdd6; }
        aramgah-spatial-landscape .lede { margin: 0.45rem 0 1rem; font-style: italic; opacity: 0.78; font-size: 0.92rem; line-height: 1.5; }
        aramgah-spatial-landscape .stage { aspect-ratio: 16 / 10; min-height: 240px; border-radius: 10px; overflow: hidden; background: #050610; }
        aramgah-spatial-landscape .stage canvas { width: 100%; height: 100%; display: block; }
        aramgah-spatial-landscape .actions { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 1rem; }
        aramgah-spatial-landscape button { font: inherit; font-size: 0.9rem; border-radius: 999px; padding: 0.5rem 1.1rem; border: 1px solid #b2bdd6; background: transparent; color: #e3e6f0; cursor: pointer; }
        aramgah-spatial-landscape button[disabled] { opacity: 0.35; cursor: not-allowed; }
        aramgah-spatial-landscape .note { margin-top: 0.8rem; font-size: 0.8rem; opacity: 0.6; line-height: 1.5; }
      </style>
      <header>
        <h3>Ninety nights, in a sky</h3>
        <span class="badge">${supported ? (vrSupported ? "VR ready" : "AR ready") : "2D fallback"}</span>
      </header>
      <p class="lede">Each star is one night of your sleep. Indigo deep, pearl light, amber rem. The moon orbits at its current phase. The amber cone is brahma-muhurta. Audio is a stereo 8 Hz delta beat.</p>
      <div class="stage" role="img" aria-label="Aramgah lunar sleep landscape preview"></div>
      <div class="actions">
        <button data-action="enter" ${supported ? "" : "disabled"}>${supported ? "Enter spatial view" : "VR/AR not supported"}</button>
        <button data-action="inline">Inline preview</button>
      </div>
      <p class="note">Pro + Spatial ₹1,499/yr enables persistent anchors and shared family-room dreaming for Vision Pro 2026 onwards.</p>
    `;
    const stage = this.querySelector(".stage") as HTMLElement;
    this.querySelector('[data-action="enter"]')?.addEventListener("click", () => void this.enter(stage, vrSupported ? "immersive-vr" : "immersive-ar"));
    this.querySelector('[data-action="inline"]')?.addEventListener("click", () => void this.inline(stage));
  }

  private async enter(stage: HTMLElement, mode: string): Promise<void> {
    try {
      const xr = navigator.xr;
      if (!xr) return;
      const session = await xr.requestSession(mode, { requiredFeatures: ["local-floor"] });
      this.currentSession = session as XRSession;
      const THREE = await loadThree();
      const renderer = new THREE.WebGLRenderer({ alpha: mode === "immersive-ar", antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      renderer.xr.enabled = true;
      stage.replaceChildren(renderer.domElement);
      await renderer.xr.setSession(this.currentSession);
      const built = await buildLunarScene(THREE);
      void playBinauralDelta();
      renderer.setAnimationLoop((t: number) => {
        built.tick(t);
        renderer.render(built.scene, built.camera);
      });
      this.currentSession.addEventListener("end", () => {
        renderer.setAnimationLoop(null);
        renderer.dispose();
        this.currentSession = null;
        void this.render();
      });
    } catch (err) {
      console.warn("[aramgah-spatial] enter failed", err);
    }
  }

  private async inline(stage: HTMLElement): Promise<void> {
    try {
      const THREE = await loadThree();
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
      const w = stage.clientWidth || 720;
      const h = stage.clientHeight || 420;
      renderer.setSize(w, h);
      stage.replaceChildren(renderer.domElement);
      const built = await buildLunarScene(THREE);
      built.camera.aspect = w / h;
      built.camera.updateProjectionMatrix();
      renderer.setAnimationLoop((t: number) => {
        built.tick(t);
        renderer.render(built.scene, built.camera);
      });
    } catch (err) {
      console.warn("[aramgah-spatial] inline failed", err);
    }
  }
}

if (!customElements.get("aramgah-spatial-landscape")) {
  customElements.define("aramgah-spatial-landscape", AramgahSpatialLandscape);
}
